/**
 * Hook: fetches an article's embedded saved queries (Phase 3 read path).
 *
 * Returns the composite payload — each embed already carries its saved
 * query's body + table_state, so the per-embed `ResearchQueryEmbed`
 * component can immediately POST to `/explorer/api/exec_query` without a
 * second lookup.
 *
 * Decoupled from `useArticle` so it can be fetched in parallel with the
 * article body and so the embed section can show its own loading state
 * (the article body shouldn't be blocked on query-engine latency).
 */

import { useEffect, useState } from "react";

import { ResearchEmbeddedQuery } from "../../../models/Research";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";

export interface UseEmbeddedQueriesResult {
  loading: boolean;
  error: string | null;
  embeddedQueries: ResearchEmbeddedQuery[];
}

export function useEmbeddedQueries(
  slug: string | undefined,
): UseEmbeddedQueriesResult {
  const api = useLeagueApi();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embeddedQueries, setEmbeddedQueries] = useState<
    ResearchEmbeddedQuery[]
  >([]);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setEmbeddedQueries([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEmbeddedQueries([]);

    api
      .getResearchEmbeddedQueries(slug)
      .then((resp) => {
        if (cancelled) return;
        setEmbeddedQueries(resp.data.embedded_queries ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // A 404 on the article surfaces here as an error — the
        // detail-page `useArticle` hook will have its own 404 state
        // driving the not-found UI, so we just record the failure.
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load embedded queries";
        setError(msg);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, slug]);

  return { loading, error, embeddedQueries };
}
